// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Pure membership-key helpers for Contact Groups, extracted from
 * ContactGroupsPage. A group member is identified by a `<type>_<id>` key, and
 * the page builds that key in several places — the add-selection toggle, the
 * "current members" set, and the browse-list exclusion filters — which must
 * all agree. Centralising the format here kills that hand-written duplication.
 *
 * `memberKey` deliberately does NOT lowercase: callers pass the already
 * normalised type. The API's `member_type` is title-case ("Customer"), so
 * `buildExistingMemberKeys` lowercases it to match the lowercase literals the
 * browse filters use ("customer" / "lead" / "contact"). No money / reseller
 * here — wall-neutral.
 */

/** The canonical `<type>_<id>` identity key for a group member. */
export function memberKey(type: string, id: string | number): string {
  return `${type}_${id}`
}

/**
 * The set of identity keys for the group's CURRENT members, lowercasing the
 * API's title-case `member_type` so the keys line up with the lowercase ones
 * the browse-exclusion filters build.
 */
export function buildExistingMemberKeys(
  members: ReadonlyArray<{ member_type: string; member_id: string | number }>,
): Set<string> {
  return new Set(members.map((m) => memberKey(m.member_type.toLowerCase(), m.member_id)))
}

/**
 * The member count the Members cell shows — and, by contract, the number the
 * server's `members` sort orders on.
 *
 * `??`, NEVER `||`. `members_count` is the LIVE count of active memberships
 * (`ContactList#members_count`); `contacts_count` is a denormalised copy kept
 * up to date only by `ContactListMembership`'s create / destroy / status
 * callbacks, so a bulk path that skips them leaves it stale. Those two really
 * can diverge — crm-api's "members ignores a STALE contacts_count column" test
 * makes them diverge on purpose. Under `||`, a group whose live count is
 * genuinely `0` is falsy and falls through to the stale column, so an EMPTY
 * group could render "999 members" while the sort correctly ranks it last.
 * `??` falls back only when the live count is actually absent from the payload.
 */
export function memberCount(
  group: { members_count?: number | null; contacts_count?: number | null },
): number {
  return group.members_count ?? group.contacts_count ?? 0
}
